import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PublicLayout from "@/components/PublicLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Store, MailCheck, XCircle } from "lucide-react";

export default function AcceptInvitation() {
  const { token } = useParams();
  const nav = useNavigate();
  const { login } = useAuth();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/invitations/${token}`)
      .then(({ data }) => setInv(data))
      .catch((e) => setError(e.response?.data?.detail || "Invitació invàlida"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Mínim 8 caràcters");
    if (password !== confirm) return toast.error("Les contrasenyes no coincideixen");
    setSubmitting(true);
    try {
      await api.post(`/invitations/${token}/accept`, { password });
      toast.success("Compte creat! Iniciant sessió…");
      await login(inv.email, password);
      nav(inv.role === "admin" ? "/admin" : "/associat");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSubmitting(false); }
  };

  if (error) return <PublicLayout><div className="max-w-md mx-auto py-24 text-center" data-testid="invite-error">
    <XCircle className="w-14 h-14 mx-auto text-destructive mb-4" />
    <div className="font-heading font-bold text-2xl">Invitació no vàlida</div>
    <p className="text-muted-foreground mt-2 text-sm">{error}</p>
  </div></PublicLayout>;
  if (!inv) return <PublicLayout><div className="text-center py-24 text-muted-foreground">Carregant…</div></PublicLayout>;

  return (
    <PublicLayout>
      <div className="max-w-md mx-auto px-6 py-16" data-testid="invite-accept-page">
        <Card className="rounded-3xl border-2 shadow-xl shadow-primary/5">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-primary text-primary-foreground flex items-center justify-center mb-4">
                <MailCheck className="w-6 h-6" />
              </div>
              <h1 className="font-heading font-bold text-2xl">Estàs convidat/da</h1>
              <p className="text-sm text-muted-foreground mt-2">
                <strong>{inv.invited_by}</strong> t'ha convidat a Shopping Salou com a{" "}
                <span className="inline-flex items-center gap-1 font-semibold text-primary">
                  {inv.role === "admin" ? <><ShieldCheck className="w-3.5 h-3.5" /> Administrador</> : <><Store className="w-3.5 h-3.5" /> Comerç associat</>}
                </span>.
              </p>
              <div className="mt-2 text-xs text-muted-foreground">Compte: <strong className="text-foreground">{inv.email}</strong></div>
            </div>
            <form onSubmit={submit} className="space-y-3" data-testid="invite-form">
              <div><Label>Nova contrasenya</Label><Input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 rounded-xl" data-testid="invite-password" /></div>
              <div><Label>Confirma la contrasenya</Label><Input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 rounded-xl" data-testid="invite-confirm" /></div>
              <Button type="submit" disabled={submitting} size="lg" className="w-full rounded-full h-12" data-testid="invite-submit">
                {submitting ? "Activant..." : "Activar el compte"}
              </Button>
              <p className="text-[11px] text-muted-foreground text-center mt-2">Mínim 8 caràcters. La invitació és d'un sol ús.</p>
            </form>
          </CardContent>
        </Card>
      </div>
    </PublicLayout>
  );
}
